/**
 * Structured Data (JSON-LD) Helper Functions
 * Generates schema.org structured data for SEO
 */

/**
 * Generate Organization structured data
 * @param {Object} data - Organization data
 * @returns {Object} JSON-LD object
 */
export const generateOrganizationSchema = (data = {}) => {
  const {
    name = localStorage.getItem('storeName') || 'Store',
    url = window.location.origin,
    logo = '',
    description = localStorage.getItem('seoMetaDescription') || '',
    contactPoint = {},
    sameAs = [] // Social media URLs
  } = data;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url
  };

  if (logo) {
    schema.logo = logo.startsWith('http') ? logo : `${url}${logo}`;
  }

  if (description) {
    schema.description = description;
  }

  if (contactPoint.telephone || contactPoint.email) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      ...contactPoint
    };
  }

  if (sameAs.length > 0) {
    schema.sameAs = sameAs;
  }

  return schema;
};

/**
 * Generate Restaurant structured data
 * @param {Object} data - Restaurant data
 * @returns {Object} JSON-LD object
 */
export const generateRestaurantSchema = (data = {}) => {
  const {
    name = '',
    image = '',
    description = '',
    address = {},
    telephone = '',
    priceRange = '',
    servesCuisine = '',
    menu = '',
    aggregateRating = null,
    openingHours = []
  } = data;

  const baseUrl = window.location.origin;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name
  };

  if (image) {
    schema.image = image.startsWith('http') ? image : `${baseUrl}${image}`;
  }

  if (description) {
    schema.description = description;
  }

  if (address.streetAddress || address.addressLocality || address.addressRegion || address.postalCode) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: address.streetAddress || '',
      addressLocality: address.addressLocality || '',
      addressRegion: address.addressRegion || '',
      postalCode: address.postalCode || '',
      addressCountry: address.addressCountry || ''
    };
  }

  if (telephone) {
    schema.telephone = telephone;
  }

  if (priceRange) {
    schema.priceRange = priceRange;
  }

  if (servesCuisine) {
    schema.servesCuisine = servesCuisine;
  }

  if (menu) {
    schema.menu = menu.startsWith('http') ? menu : `${baseUrl}${menu}`;
  }

  if (aggregateRating && aggregateRating.ratingValue) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.ratingValue,
      reviewCount: aggregateRating.reviewCount || 0,
      bestRating: aggregateRating.bestRating || 5,
      worstRating: aggregateRating.worstRating || 1
    };
  }

  if (openingHours && openingHours.length > 0) {
    schema.openingHoursSpecification = openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: hours.dayOfWeek || [],
      opens: hours.opens || '',
      closes: hours.closes || ''
    }));
  }

  return schema;
};

/**
 * Generate Product structured data
 * @param {Object} data - Product data
 * @returns {Object} JSON-LD object
 */
export const generateProductSchema = (data = {}) => {
  const {
    name = '',
    description = '',
    image = [],
    sku = '',
    brand = {},
    offers = {},
    aggregateRating = null,
    category = ''
  } = data;

  const baseUrl = window.location.origin;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name
  };

  if (description) {
    schema.description = description;
  }

  // Handle single image or array of images
  if (image) {
    if (Array.isArray(image)) {
      schema.image = image.map(img => 
        img.startsWith('http') ? img : `${baseUrl}${img}`
      );
    } else {
      schema.image = image.startsWith('http') ? image : `${baseUrl}${image}`;
    }
  }

  if (sku) {
    schema.sku = sku;
  }

  if (brand.name) {
    schema.brand = {
      '@type': 'Brand',
      name: brand.name
    };
    if (brand.logo) {
      schema.brand.logo = brand.logo.startsWith('http') ? brand.logo : `${baseUrl}${brand.logo}`;
    }
  }

  if (offers.price !== undefined || offers.availability) {
    schema.offers = {
      '@type': 'Offer',
      price: offers.price || 0,
      priceCurrency: offers.priceCurrency || 'USD',
      availability: offers.availability || 'https://schema.org/InStock',
      url: offers.url || window.location.href
    };
  }

  if (aggregateRating && aggregateRating.ratingValue) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.ratingValue,
      reviewCount: aggregateRating.reviewCount || 0,
      bestRating: aggregateRating.bestRating || 5,
      worstRating: aggregateRating.worstRating || 1
    };
  }

  if (category) {
    schema.category = category;
  }

  return schema;
};

/**
 * Generate BreadcrumbList structured data
 * @param {Array} items - Breadcrumb items [{name, url}, ...]
 * @returns {Object} JSON-LD object
 */
export const generateBreadcrumbSchema = (items = []) => {
  if (!items || items.length === 0) return null;

  const baseUrl = window.location.origin;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`
    }))
  };

  return schema;
};

/**
 * Generate LocalBusiness structured data
 * @param {Object} data - LocalBusiness data
 * @returns {Object} JSON-LD object
 */
export const generateLocalBusinessSchema = (data = {}) => {
  const {
    name = localStorage.getItem('storeName') || 'Store',
    image = '',
    description = '',
    address = {},
    telephone = '',
    priceRange = '',
    openingHours = [],
    aggregateRating = null,
    geo = {}
  } = data;

  const baseUrl = window.location.origin;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name
  };

  if (image) {
    schema.image = image.startsWith('http') ? image : `${baseUrl}${image}`;
  }

  if (description) {
    schema.description = description;
  }

  if (address.streetAddress || address.addressLocality) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: address.streetAddress || '',
      addressLocality: address.addressLocality || '',
      addressRegion: address.addressRegion || '',
      postalCode: address.postalCode || '',
      addressCountry: address.addressCountry || ''
    };
  }

  if (telephone) {
    schema.telephone = telephone;
  }

  if (priceRange) {
    schema.priceRange = priceRange;
  }

  if (openingHours && openingHours.length > 0) {
    schema.openingHoursSpecification = openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: hours.dayOfWeek || [],
      opens: hours.opens || '',
      closes: hours.closes || ''
    }));
  }

  if (geo.latitude && geo.longitude) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: geo.latitude,
      longitude: geo.longitude
    };
  }

  if (aggregateRating && aggregateRating.ratingValue) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.ratingValue,
      reviewCount: aggregateRating.reviewCount || 0
    };
  }

  return schema;
};

/**
 * Generate WebSite structured data with search functionality
 * @param {Object} data - WebSite data
 * @returns {Object} JSON-LD object
 */
export const generateWebSiteSchema = (data = {}) => {
  const {
    name = localStorage.getItem('storeName') || 'Store',
    url = window.location.origin,
    description = localStorage.getItem('seoMetaDescription') || '',
    potentialAction = {}
  } = data;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url
  };

  if (description) {
    schema.description = description;
  }

  if (potentialAction.target) {
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: potentialAction.target
      },
      'query-input': 'required name=search_term_string'
    };
  }

  return schema;
};

/**
 * Generate Article structured data for content pages
 * @param {Object} data - Article data
 * @returns {Object} JSON-LD object
 */
export const generateArticleSchema = (data = {}) => {
  const {
    headline = '',
    description = '',
    image = '',
    datePublished = '',
    dateModified = '',
    author = {},
    publisher = {}
  } = data;

  const baseUrl = window.location.origin;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline
  };

  if (description) {
    schema.description = description;
  }

  if (image) {
    schema.image = image.startsWith('http') ? image : `${baseUrl}${image}`;
  }

  if (datePublished) {
    schema.datePublished = datePublished;
  }

  if (dateModified) {
    schema.dateModified = dateModified;
  }

  if (author.name) {
    schema.author = {
      '@type': 'Person',
      name: author.name
    };
  }

  if (publisher.name) {
    schema.publisher = {
      '@type': 'Organization',
      name: publisher.name
    };
    if (publisher.logo) {
      schema.publisher.logo = {
        '@type': 'ImageObject',
        url: publisher.logo.startsWith('http') ? publisher.logo : `${baseUrl}${publisher.logo}`
      };
    }
  }

  return schema;
};

